import { useScopedIncident } from 'components/IAP/hooks/incidentHooks';
import {
  ICS_FORM_FAMILY_LABELS,
  isFormImplementedForFamily,
  resolveIncidentFormFamily,
  supportsDocxForFamily,
  toBaseFormId,
  type IcsFormFamily,
} from 'components/IAP/formFamily';

/**
 * Which authority's ICS forms this incident is authored against. Incidents
 * created before the family was selectable fall back to the Coast Guard forms
 * they were authored with.
 */
export function useIcsFormFamily(): IcsFormFamily {
  return resolveIncidentFormFamily(useScopedIncident());
}

export type IcsFormAvailability = {
  available: boolean;
  /** Tooltip copy for a disabled export control; empty when available. */
  reason: string;
  /** Whether this form can also be exported as Word for this family. */
  supportsDocx: boolean;
};

/**
 * Whether this incident's authority publishes the given form. Callers name
 * Coast Guard forms, so the name is reduced to a base id before the check.
 *
 * Export controls consult this rather than letting the request fail: the
 * server-side guard is the backstop, not the user-facing answer.
 */
export function useIcsFormAvailability(
  formname: string | undefined
): IcsFormAvailability {
  const family = useIcsFormFamily();
  if (!formname) return { available: true, reason: '', supportsDocx: false };

  const baseId = toBaseFormId(formname);
  const supportsDocx = supportsDocxForFamily(baseId, family);
  if (isFormImplementedForFamily(baseId, family)) {
    return { available: true, reason: '', supportsDocx };
  }
  return {
    available: false,
    reason: `${baseId.replace('_', ' ')} is not published for ${ICS_FORM_FAMILY_LABELS[family]} incidents.`,
    supportsDocx: false,
  };
}
